import { Button, Container, ContainerArgs, Label, NumericInput, VectorInput } from '@playcanvas/pcui';
import { Quat, Vec3 } from 'playcanvas';

import { EnvironmentRotationOp, MultiOp, SplatImportNormalizationOp, type EditOp } from '../edit-ops';
import { ElementType } from '../element';
import { Events } from '../events';
import { Pivot } from '../pivot';
import { Transform as SceneTransform } from '../transform';
import { Scene } from '../scene';
import type { VehicleMotionController } from '../vehicle-motion-controller';
import { localize } from './localization';

const v = new Vec3();

class Transform extends Container {
    constructor(events: Events, args: ContainerArgs = {}) {
        args = {
            ...args,
            id: 'transform'
        };

        super(args);

        const superDestroy = this.destroy?.bind(this) || (() => {});

        // position
        const position = new Container({
            class: 'transform-row'
        });

        const positionLabel = new Label({
            class: 'transform-label',
            text: localize('panel.scene-manager.transform.position')
        });

        const positionVector = new VectorInput({
            class: 'transform-expand',
            precision: 3,
            dimensions: 3,
            placeholder: ['X', 'Y', 'Z'],
            value: [0, 0, 0],
            enabled: false
        });

        const positionReset = new Button({
            text: '0',
            class: 'transform-reset-btn',
            enabled: false
        });

        position.append(positionLabel);
        position.append(positionVector);
        position.append(positionReset);

        // rotation
        const rotation = new Container({
            class: 'transform-row'
        });

        const rotationLabel = new Label({
            class: 'transform-label',
            text: localize('panel.scene-manager.transform.rotation')
        });

        const rotationVector = new VectorInput({
            class: 'transform-expand',
            precision: 2,
            dimensions: 3,
            placeholder: ['X', 'Y', 'Z'],
            value: [0, 0, 0],
            enabled: false
        });

        const rotationReset = new Button({
            text: '0',
            class: 'transform-reset-btn',
            enabled: false
        });

        rotation.append(rotationLabel);
        rotation.append(rotationVector);
        rotation.append(rotationReset);

        // scale
        const scale = new Container({
            class: 'transform-row'
        });

        const scaleLabel = new Label({
            class: 'transform-label',
            text: localize('panel.scene-manager.transform.scale')
        });

        const scaleVector = new VectorInput({
            class: 'transform-expand',
            precision: 3,
            dimensions: 3,
            placeholder: ['X', 'Y', 'Z'],
            value: [1, 1, 1],
            min: 0.001,
            max: 10000,
            enabled: false
        });

        scale.append(scaleLabel);
        scale.append(scaleVector);

        this.append(position);
        this.append(rotation);
        this.append(scale);

        // vehicle motion summary (keyframes live on VehicleMotionController, not on the helper TRS)
        const vehicleMotion = new Container({
            class: 'transform-row',
            hidden: true
        });

        const vehicleMotionLabel = new Label({
            class: 'transform-label',
            text: localize('panel.scene-manager.transform.motion')
        });

        const vehicleMotionText = new Label({
            class: ['transform-expand', 'vehicle-motion-text'],
            text: ''
        });

        const vehicleMotionInfo = new Button({
            text: 'i',
            class: 'transform-reset-btn',
            enabled: false
        });

        const vehicleMotionCopy = new Button({
            text: 'C',
            class: 'transform-reset-btn',
            enabled: false
        });

        vehicleMotion.append(vehicleMotionLabel);
        vehicleMotion.append(vehicleMotionText);
        vehicleMotion.append(vehicleMotionInfo);
        vehicleMotion.append(vehicleMotionCopy);
        this.append(vehicleMotion);

        const motionTarget = new Container({
            class: 'transform-row',
            hidden: true
        });

        const motionTargetLabel = new Label({
            class: 'transform-label',
            text: 'Target'
        });

        const motionTargetText = new Label({
            class: ['transform-expand', 'vehicle-motion-text'],
            text: ''
        });

        const motionTargetSelect = new Button({
            text: 'Go',
            class: 'transform-reset-btn',
            enabled: false,
            hidden: true
        });

        motionTarget.append(motionTargetLabel);
        motionTarget.append(motionTargetText);
        motionTarget.append(motionTargetSelect);
        this.append(motionTarget);

        let currentMotionSummary: { text: string; tracks: any } | null = null;
        let currentMotionTarget: { kind: 'vehicle' | 'camera'; root: any; target: any } | null = null;

        vehicleMotionInfo.on('click', async () => {
            if (!currentMotionSummary) return;
            const msg = [
                'Imported vehicle motion is stored as keyframe tracks (not as changes to the root helper’s TRS).',
                'Root helpers (*_root) are intended as a stable offset you can align; animated helpers (*_body / *_wheelN) are driven by the tracks.',
                '',
                currentMotionSummary.text
            ].join('\n');
            await events.invoke('showPopup', {
                type: 'info',
                header: 'Vehicle Motion',
                message: msg
            });
        });

        vehicleMotionCopy.on('click', async () => {
            if (!currentMotionSummary) return;
            try {
                await navigator.clipboard.writeText(JSON.stringify(currentMotionSummary.tracks));
                events.fire('status.text', 'Copied motion tracks JSON', { timeoutMs: 2000 });
            } catch (e) {
                events.fire('status.text', 'Failed to copy motion tracks', { timeoutMs: 2000 });
            }
        });

        motionTargetSelect.on('click', () => {
            if (!currentMotionTarget?.target) return;
            events.fire('selection', currentMotionTarget.target);
        });

        // splat import normalization quick-fix
        const normalization = new Container({
            class: 'transform-row',
            hidden: true
        });

        const normalizationLabel = new Label({
            class: 'transform-label',
            text: 'Import'
        });

        const normalizationButtons = new Container({
            class: 'transform-expand',
            flex: true,
            flexDirection: 'row'
        });

        const rotPlusX = new Button({
            text: '+90X'
        });
        const rotMinusX = new Button({
            text: '-90X'
        });

        normalizationButtons.append(rotPlusX);
        normalizationButtons.append(rotMinusX);
        normalization.append(normalizationLabel);
        normalization.append(normalizationButtons);
        this.append(normalization);

        const sceneAlignment = new Container({
            class: 'transform-row',
            hidden: true
        });

        const sceneAlignmentLabel = new Label({
            class: 'transform-label',
            text: 'Scene+Sky'
        });

        const sceneAlignmentButtons = new Container({
            class: 'transform-expand',
            flex: true,
            flexDirection: 'row'
        });

        const scenePlusX = new Button({
            text: '+90X'
        });
        const sceneMinusX = new Button({
            text: '-90X'
        });

        sceneAlignmentButtons.append(scenePlusX);
        sceneAlignmentButtons.append(sceneMinusX);
        sceneAlignment.append(sceneAlignmentLabel);
        sceneAlignment.append(sceneAlignmentButtons);
        this.append(sceneAlignment);

        const toArray = (v: Vec3) => {
            return [v.x, v.y, v.z];
        };

        let uiUpdating = false;
        let mouseUpdating = false;

        const selectionTransform = new SceneTransform();

        // update UI with a scene transform
        const updateUIFromTransform = (transform: SceneTransform) => {
            uiUpdating = true;
            transform.rotation.getEulerAngles(v);
            positionVector.value = toArray(transform.position);
            rotationVector.value = toArray(v);
            scaleVector.value = toArray(transform.scale);
            uiUpdating = false;
        };

        // update UI with pivot
        const updateUI = (pivot: Pivot) => {
            updateUIFromTransform(pivot.transform);
        };

        // update pivot with UI
        const updatePivot = (pivot: Pivot) => {
            const p = positionVector.value;
            const r = rotationVector.value;
            const q = new Quat().setFromEulerAngles(r[0], r[1], r[2]);
            const s = scaleVector.value;

            if (q.w < 0) {
                q.mulScalar(-1);
            }

            pivot.moveTRS(new Vec3(p[0], p[1], p[2]), q, new Vec3(s[0], s[1], s[2]));
        };

        // handle a change in the UI state
        const change = () => {
            if (!uiUpdating) {
                const pivot = events.invoke('pivot') as Pivot;
                if (mouseUpdating) {
                    updatePivot(pivot);
                } else {
                    pivot.start();
                    updatePivot(pivot);
                    pivot.end();
                }
            }
        };

        const mousedown = () => {
            mouseUpdating = true;
            const pivot = events.invoke('pivot') as Pivot;
            pivot.start();
        };

        const mouseup = () => {
            const pivot = events.invoke('pivot') as Pivot;
            updatePivot(pivot);
            mouseUpdating = false;
            pivot.end();
        };

        [positionVector.inputs, rotationVector.inputs, scaleVector.inputs].flat().forEach((input) => {
            input.on('change', change);
            input.on('slider:mousedown', mousedown);
            input.on('slider:mouseup', mouseup);
        });

        positionReset.on('click', () => {
            if (!positionVector.enabled) return;
            positionVector.value = [0, 0, 0];
            change();
        });

        rotationReset.on('click', () => {
            if (!rotationVector.enabled) return;
            rotationVector.value = [0, 0, 0];
            change();
        });

        const getVehicleMotionSummary = (controller: VehicleMotionController) => {
            try {
                const tracks = controller.getTracks();
                const b = tracks.body?.frames?.length ?? 0;
                const w = (tracks.wheels ?? []).map(t => t?.frames?.length ?? 0);
                const maxFrame = (() => {
                    const frames = tracks.body?.frames;
                    if (!Array.isArray(frames) || frames.length === 0) return 0;
                    return frames[frames.length - 1];
                })();
                return {
                    text: `Body ${b} keys, Wheels ${w.join('/')}, last @ frame ${Math.round(maxFrame)}`,
                    tracks
                };
            } catch (e) {
                return { text: 'Motion tracks unavailable', tracks: null as any };
            }
        };

        const updateMotionTargetUI = (selection: any) => {
            const motionKind = selection ? events.invoke('importedMotion.kind', selection) as 'vehicle' | 'camera' | null : null;
            const motionRoot = motionKind ? events.invoke('importedMotion.root', selection) as any : null;
            const displayTarget = motionKind ? events.invoke('importedMotion.displayTarget', selection) as any : null;

            if (!motionKind || !motionRoot || !displayTarget) {
                currentMotionTarget = null;
                motionTarget.hidden = true;
                motionTargetText.text = '';
                motionTargetSelect.enabled = false;
                motionTargetSelect.hidden = true;
                return;
            }

            currentMotionTarget = {
                kind: motionKind,
                root: motionRoot,
                target: displayTarget
            };

            const kindLabel = motionKind === 'vehicle' ? 'Vehicle' : 'Camera';
            const targetName = String(displayTarget.name ?? 'body');
            motionTarget.hidden = false;
            motionTargetText.text = `${kindLabel}: ${targetName}${selection === displayTarget ? ' (selected)' : ''}`;
            motionTargetSelect.hidden = selection !== motionRoot;
            motionTargetSelect.enabled = selection === motionRoot;
        };

        const updateSelectionTransform = (selection: any) => {
            if (!selection) return;

            if (selection?.type === ElementType.splat && selection.getPivot) {
                const origin = events.invoke('pivot.origin');
                selection.getPivot(origin === 'center' ? 'center' : 'boundCenter', false, selectionTransform);
                updateUIFromTransform(selectionTransform);
                return;
            }

            const pos = selection?.pivot?.getLocalPosition?.() ??
                selection?.entity?.getLocalPosition?.() ??
                selection?.getLocalPosition?.();
            const rot = selection?.pivot?.getLocalRotation?.() ??
                selection?.entity?.getLocalRotation?.() ??
                selection?.getLocalRotation?.();
            const scl = selection?.pivot?.getLocalScale?.() ??
                selection?.entity?.getLocalScale?.() ??
                selection?.getLocalScale?.();
            selectionTransform.set(
                pos ?? new Vec3(),
                rot ?? new Quat(),
                scl ?? new Vec3(1, 1, 1)
            );
            updateUIFromTransform(selectionTransform);
        };

        const isAnimatedImportedMotionHelper = (selection: any) => {
            const motionRoot = selection ? events.invoke('importedMotion.root', selection) as any : null;
            return !!motionRoot && selection !== motionRoot && !!selection?.animated;
        };

        const unbindInputs = () => {
            [positionVector.inputs, rotationVector.inputs, scaleVector.inputs].flat().forEach((input) => {
                input.unbind('change');
                input.unbind('slider:mousedown');
                input.unbind('slider:mouseup');
            });
        };

        // toggle ui availability based on selection
        const onSelectionChanged = (selection: any) => {
            positionVector.enabled = rotationVector.enabled = scaleVector.enabled = positionReset.enabled = rotationReset.enabled = !!selection;
            normalization.hidden = !(selection?.type === ElementType.splat);
            sceneAlignment.hidden = !(selection?.type === ElementType.splat);

            // Vehicle motion UI is independent of pivot binding (we only read controller tracks).
            const motionKind = selection ? events.invoke('importedMotion.kind', selection) as 'vehicle' | 'camera' | null : null;
            const motionRoot = motionKind === 'vehicle' ? (events.invoke('importedMotion.root', selection) as any) : null;
            const controller = motionRoot ? ((motionRoot as any).vehicleMotionController as VehicleMotionController | undefined) : undefined;
            if (controller && typeof controller.getTracks === 'function') {
                const summary = getVehicleMotionSummary(controller);
                currentMotionSummary = summary;
                vehicleMotion.hidden = false;
                vehicleMotionText.text = summary.text;
                vehicleMotionInfo.enabled = true;
                vehicleMotionCopy.enabled = !!(navigator as any)?.clipboard;
            } else {
                currentMotionSummary = null;
                vehicleMotion.hidden = true;
                vehicleMotionText.text = '';
                vehicleMotionInfo.enabled = false;
                vehicleMotionCopy.enabled = false;
            }

            updateMotionTargetUI(selection);

            if (!selection) return;

            updateSelectionTransform(selection);
        };
        events.on('selection.changed', onSelectionChanged);

        const onTimelineChanged = () => {
            const selection = events.invoke('selection') as any;
            if (!isAnimatedImportedMotionHelper(selection)) {
                return;
            }

            updateSelectionTransform(selection);
        };
        events.on('timeline.frame', onTimelineChanged);
        events.on('timeline.time', onTimelineChanged);

        const applyNormalizationDelta = (deltaEuler: Vec3, includeEnvironment: boolean) => {
            const selection = events.invoke('selection') as any;
            if (!selection || selection.type !== ElementType.splat) {
                return;
            }

            const splat = selection as any;
            const oldRot = splat.importNormalizationRotation as Quat;
            const delta = new Quat().setFromEulerAngles(deltaEuler.x, deltaEuler.y, deltaEuler.z);
            const newRot = new Quat().mul2(delta, oldRot);

            const ops: EditOp[] = [new SplatImportNormalizationOp({ splat, oldRot, newRot })];
            const environment = events.invoke('environment.get') as any;
            const scene = (splat.scene ?? null) as Scene | null;

            if (includeEnvironment && scene && environment && (environment.mode !== 'none' || environment.enabled)) {
                const oldEuler = Array.isArray(environment.scene_rotation_euler_deg)
                    ? environment.scene_rotation_euler_deg as [number, number, number]
                    : [0, 0, 0] as [number, number, number];
                const newEuler: [number, number, number] = [
                    oldEuler[0] + deltaEuler.x,
                    oldEuler[1] + deltaEuler.y,
                    oldEuler[2] + deltaEuler.z
                ];

                ops.push(new EnvironmentRotationOp({
                    scene,
                    oldEuler,
                    newEuler
                }));
            }

            events.fire('edit.add', ops.length === 1 ? ops[0] : new MultiOp(ops));
        };

        rotPlusX.on('click', () => applyNormalizationDelta(new Vec3(90, 0, 0), false));
        rotMinusX.on('click', () => applyNormalizationDelta(new Vec3(-90, 0, 0), false));
        scenePlusX.on('click', () => applyNormalizationDelta(new Vec3(90, 0, 0), true));
        sceneMinusX.on('click', () => applyNormalizationDelta(new Vec3(-90, 0, 0), true));

        const onPivotPlaced = (pivot: Pivot) => {
            updateUI(pivot);
        };
        events.on('pivot.placed', onPivotPlaced);

        const onPivotMoved = (pivot: Pivot) => {
            if (!mouseUpdating) {
                updateUI(pivot);
            }
        };
        events.on('pivot.moved', onPivotMoved);

        const onPivotEnded = (pivot: Pivot) => {
            // Ensure mouseUpdating is cleared even if mouseup wasn't fired
            // (can happen if mouse released outside slider element)
            mouseUpdating = false;
            updateUI(pivot);
        };
        events.on('pivot.ended', onPivotEnded);

        this.destroy = () => {
            events.off('selection.changed', onSelectionChanged);
            events.off('timeline.frame', onTimelineChanged);
            events.off('timeline.time', onTimelineChanged);
            events.off('pivot.placed', onPivotPlaced);
            events.off('pivot.moved', onPivotMoved);
            events.off('pivot.ended', onPivotEnded);
            unbindInputs();
            superDestroy();
        };
    }
}

export { Transform };
